/**
 * One trending TikTok song as a card. Top: a uniform 9:16 media area for the song's top TikTok video. Cards never
 * auto-mount TikTok iframes (a grid of them trips TikTok's CDN firewall): the first video shows as a poster (its oEmbed
 * thumbnail, a TikTok glyph, a centred play badge and the creator's @handle); clicking it mounts THAT video's player in
 * place, and only one card is live at a time — starting another (or scrolling far away) returns it to its poster, and a
 * small ✕ does too. A video without a thumbnail shows the cover fallback with a "Watch on card" pill that does the same.
 * No video (sample songs, none found, request failed): the cover at half size over the same cover blurred — a generated
 * genre tile when the cover is missing or TikTok's shared default artwork. While the videos load: a light skeleton.
 * The videos request fires once the card is near the viewport.
 * Below: "#rank title" + movement / artist · genre + status / a full-width 30-day sparkline, then ONE "Play song"
 * (the song preview in the shared player, with its length) directly above "Remix" (outline — opens the Remix dialog)
 * and › for details. Clicking the card body (title / meta) or › calls `onOpen` — the right-hand stage on desktop, the song drawer
 * below 1024px. Rights are said once, in the Trending header note (and on the stage / in the drawer), not per card.
 */
import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent, type RefObject } from 'react';
import { claimTikTokPlayer, releaseTikTokPlayer, resetTikTokPlayers, useTikTokPlayer } from '@/components/tiktok/playerSlot';
import { ChevronRight, Pause, Play, Wand2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/components/ui/utils';
import { LazyTikTokEmbed } from '@/components/tiktok/LazyTikTokEmbed';
import { TIKTOK_PLAY_ALLOW, tiktokPreviewEmbedUrl } from '@/components/tiktok/embedUrl';
import type { AudioTrend, TrendTopVideo } from '@/api/audioStudioApi';
import { usePlayer } from './AudioPlayer';
import { CoverFallback } from './CoverArt';
import { compactNumber, formatClock } from './format';
import { trendHasVideoSource, useTrendVideos } from './queries';
import { RankChange, TrendSparkline, TrendStatusText } from './TrendBits';
import { formatGrowth, trendGenres, trendHasViews, trendPlayable, trendSparkline, trendViews7d } from './trendInsights';

/**
 * Tracks whether an element is near the viewport. `seen` latches once it has been; `near` follows it both ways.
 * Without IntersectionObserver (old browsers, tests) everything counts as visible.
 */
// eslint-disable-next-line react-refresh/only-export-components -- small hook owned by the card
export function useNearViewport(ref: RefObject<Element | null>, rootMargin = '600px 0px'): { near: boolean; seen: boolean } {
  const supported = typeof IntersectionObserver !== 'undefined';
  const [near, setNear] = useState(!supported);
  const [seen, setSeen] = useState(!supported);
  useEffect(() => {
    const el = ref.current;
    if (!supported || !el) return;
    const io = new IntersectionObserver((entries) => {
      const hit = entries.some((e) => e.isIntersecting);
      setNear(hit);
      if (hit) setSeen(true);
    }, { rootMargin });
    io.observe(el);
    return () => io.disconnect();
  }, [ref, rootMargin, supported]);
  return { near, seen };
}

/** Keeps clicks on the card's own controls from also opening the drawer. */
const stop = (e: MouseEvent) => e.stopPropagation();

// ── One live card video at a time ─────────────────────────────────────────────
// A card plays its TikTok video inline only while it holds the page's single player slot (playerSlot.ts); starting
// another card, or opening a song on the stage / in the drawer, returns this card to its poster.
const cardKey = (trendId: string) => `card:${trendId}`;
const useLiveCard = (trendId: string) => useTikTokPlayer(cardKey(trendId));
/** Tests: every card back to its poster. */
// eslint-disable-next-line react-refresh/only-export-components -- test hook owned by the card
export function resetLiveCardVideo() { resetTikTokPlayers(); }

function TikTokGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden className={className} fill="currentColor">
      <path d="M19.589 6.686a4.793 4.793 0 0 1-3.77-4.245V2h-3.445v13.672a2.896 2.896 0 0 1-5.201 1.743 2.895 2.895 0 0 1 3.183-4.51v-3.5a6.329 6.329 0 0 0-5.394 10.692 6.33 6.33 0 0 0 10.857-4.424V8.687a8.182 8.182 0 0 0 4.773 1.526V6.79a4.831 4.831 0 0 1-1.003-.104z" />
    </svg>
  );
}

const handle = (author: string | null | undefined) => (author ? (author.startsWith('@') ? author : `@${author}`) : null);

/** The card's 9:16 media: a video poster (click to play in place), else the cover fallback; skeleton while loading. */
function CardMedia({ t, region, placeholderCover }: { t: AudioTrend; region: string; placeholderCover: boolean }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const { near, seen } = useNearViewport(ref);
  const live = useLiveCard(t.trend_id);
  const { isPlaying, pauseResume } = usePlayer();
  const inline = t.top_videos ?? [];
  const source = inline.length === 0 && trendHasVideoSource(t.trend_id);
  const q = useTrendVideos(t.trend_id, region, seen && source);
  const video: TrendTopVideo | undefined = (inline.length ? inline : q.data ?? [])[0];
  const loading = !video && source && !q.isError && (!seen || q.isPending);
  const cover = placeholderCover ? null : t.cover_url;
  const genre = trendGenres(t)[0] ?? null;
  const by = handle(video?.author);

  // Scrolled far away while live: back to the poster (drops the iframe).
  useEffect(() => {
    if (live && !near) releaseTikTokPlayer(cardKey(t.trend_id));
  }, [live, near]);
  // Leaving the page (or the card) with its video live: nothing stays marked live.
  useEffect(() => () => releaseTikTokPlayer(cardKey(t.trend_id)), [t.trend_id]);

  const start = () => {
    // The video and the song preview must not play over each other.
    if (isPlaying) pauseResume();
    claimTikTokPlayer(cardKey(t.trend_id));
  };
  const watchLabel = `Play the top TikTok video${by ? ` by ${by}` : ''} using ${t.title}`;

  return (
    <div ref={ref} data-media={video ? (live ? 'live' : 'poster') : loading ? 'loading' : 'cover'} className="relative aspect-[9/16] w-full overflow-hidden rounded-t-[12px] bg-[#f2f4f7]">
      {video && live ? (
        <>
          <LazyTikTokEmbed
            src={tiktokPreviewEmbedUrl(video, { autoplay: true })}
            title={`Top TikTok video using ${t.title}`}
            lazy={false}
            allow={TIKTOK_PLAY_ALLOW}
            placeholder={null}
            className="h-full w-full bg-[#f2f4f7]"
            iframeClassName="h-full w-full border-0"
          />
          <button
            type="button"
            onClick={() => releaseTikTokPlayer(cardKey(t.trend_id))}
            aria-label={`Close the video for ${t.title}`}
            className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-sm hover:bg-black/70"
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        </>
      ) : video && video.thumbnail_url ? (
        <button type="button" onClick={start} aria-label={watchLabel} title={video.caption || undefined} className="group/poster relative block h-full w-full">
          <img src={video.thumbnail_url} alt="" className="h-full w-full object-cover" loading="lazy" />
          <span className="absolute left-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-sm" aria-hidden>
            <TikTokGlyph className="h-3.5 w-3.5" />
          </span>
          <span className="absolute inset-0 m-auto flex h-11 w-11 items-center justify-center rounded-full bg-black/45 text-white backdrop-blur-sm transition-colors group-hover/poster:bg-black/60" aria-hidden>
            <Play className="ml-0.5 h-5 w-5" />
          </span>
          {by && (
            <span className="absolute inset-x-0 bottom-0 truncate bg-gradient-to-t from-black/60 to-transparent px-2 pb-2 pt-6 text-left text-[11.5px] leading-4 text-white" style={{ fontWeight: 600 }}>
              {by}
            </span>
          )}
        </button>
      ) : loading ? (
        <div role="status" aria-label={`Loading the top TikTok video for ${t.title}`} className="h-full w-full animate-pulse bg-[#f2f4f7]" />
      ) : (
        <>
          <CoverFallback coverUrl={cover} genre={genre} title={t.title} />
          {/* No video to show: the centred play button plays the SONG (same as "Play song" below). */}
          {!video && <SongOverlayButton t={t} />}
          {video && (
            <button
              type="button"
              onClick={start}
              aria-label={watchLabel}
              className="absolute bottom-2 left-1/2 inline-flex h-6 -translate-x-1/2 items-center gap-1 whitespace-nowrap rounded-full bg-black/55 px-2 text-[11.5px] leading-4 text-white backdrop-blur-sm hover:bg-black/70"
              style={{ fontWeight: 600 }}
            >
              <Play className="h-3 w-3" aria-hidden /> Watch on card
            </button>
          )}
        </>
      )}
    </div>
  );
}

/** Centred play/pause over a video-less card's cover — mirrors the poster's play badge, but plays the song preview. */
function SongOverlayButton({ t }: { t: AudioTrend }) {
  const { current, isPlaying, toggle } = usePlayer();
  const item = trendPlayable(t);
  const playing = current?.id === item.id && isPlaying;
  return (
    <button
      type="button"
      onClick={() => toggle(item)}
      disabled={!item.audioUrl}
      aria-label={playing ? `Pause ${t.title}` : `Play the song ${t.title}`}
      title={item.audioUrl ? undefined : 'Preview refreshing — try again shortly'}
      className="group/song absolute inset-0 flex items-center justify-center disabled:cursor-not-allowed"
    >
      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-black/45 text-white backdrop-blur-sm transition-colors group-hover/song:bg-black/60 group-disabled/song:opacity-50" aria-hidden>
        {playing ? <Pause className="h-5 w-5" /> : <Play className="ml-0.5 h-5 w-5" />}
      </span>
    </button>
  );
}

/** The song preview in the shared player — disabled (with a tooltip) while the cached TikTok preview is refreshing. */
function PlaySongButton({ t }: { t: AudioTrend }) {
  const { current, isPlaying, toggle } = usePlayer();
  const item = trendPlayable(t);
  const playing = current?.id === item.id && isPlaying;
  const button = (
    <Button
      type="button"
      size="sm"
      variant="ghost"
      onClick={() => toggle(item)}
      disabled={!item.audioUrl}
      aria-label={playing ? `Pause ${t.title}` : `Play ${t.title}`}
      className="h-8 w-full justify-start gap-1.5 rounded-[8px] px-2 text-[12.5px] leading-[18px] text-[#344054] hover:bg-[#f2f4f7]"
    >
      {playing ? <Pause className="h-3.5 w-3.5" aria-hidden /> : <Play className="h-3.5 w-3.5" aria-hidden />}
      {playing ? 'Pause' : 'Play song'}
      {t.duration_s ? <span className="ml-auto tabular-nums text-[#98a2b3]">{formatClock(t.duration_s)}</span> : null}
    </Button>
  );
  if (item.audioUrl) return button;
  // No preview_url = the cached TikTok preview expired (refreshes within hours). A disabled button gets no pointer
  // events, so the tooltip hangs off a focusable wrapper.
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span tabIndex={0} className="block w-full rounded-[8px] outline-none focus-visible:ring-2 focus-visible:ring-[#98a2b3]/40">{button}</span>
      </TooltipTrigger>
      <TooltipContent side="top" className="bg-[#101828] text-[11.5px] leading-4 text-white">Preview refreshing</TooltipContent>
    </Tooltip>
  );
}

export function TrendCard({ t, region, selected = false, placeholderCover = false, onOpen, onRemix }: {
  t: AudioTrend;
  region: string;
  /** This song is open on the stage (subtle ring). */
  selected?: boolean;
  /** Show the generated genre tile instead of `cover_url` (missing or shared default artwork). */
  placeholderCover?: boolean;
  /** Card body and ›: open the song on the stage / in the drawer. */
  onOpen: () => void;
  /** "Remix": open the Remix dialog for this song. */
  onRemix: () => void;
}) {
  const genres = trendGenres(t);
  const spark = useMemo(() => trendSparkline(t.history), [t.history]);
  const views7d = trendHasViews(t) ? trendViews7d(t) : null;
  const growth = formatGrowth(t.growth_pct);
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onOpen(); }
  };
  const sparkLabel = spark?.kind === 'views'
    ? `Daily views for ${t.title}${views7d != null ? ` — ${compactNumber(views7d)} in 7 days${growth ? `, ${growth}` : ''}` : ''}`
    : `Daily rank for ${t.title} (up is better)`;

  return (
    <article
      aria-label={t.title}
      aria-current={selected ? 'true' : undefined}
      data-selected={selected ? 'true' : undefined}
      className={cn(
        '@container flex h-full w-full flex-col overflow-hidden rounded-[12px] border bg-white transition-[border-color,box-shadow]',
        selected ? 'border-[#98a2b3]' : 'border-[#eaecf0] hover:border-[#d0d5dd]',
        selected && 'ring-2 ring-[#eaecf0] ring-offset-1',
      )}
    >
      <CardMedia t={t} region={region} placeholderCover={placeholderCover} />

      <div
        tabIndex={0}
        onClick={onOpen}
        onKeyDown={onKey}
        aria-label={`${t.title} — open details`}
        className="flex flex-1 cursor-pointer flex-col gap-1 p-3 outline-none focus-visible:bg-[#f9fafb]"
      >
        {/* Line 1: #rank title · movement */}
        <div className="flex items-baseline gap-1.5 text-[14px] leading-5" style={{ fontWeight: 600 }}>
          <span className="shrink-0 tabular-nums text-[#98a2b3]">#{t.rank}</span>
          <span className="min-w-0 flex-1 truncate text-[#101828]" title={t.title}>{t.title}</span>
          <span className="shrink-0 self-center"><RankChange v={t.rank_change} /></span>
        </div>
        {/* Line 2: artist · genre + status */}
        <div className="flex items-center gap-2">
          <span className="min-w-0 flex-1 truncate text-[12.5px] leading-[18px] text-[#667085]">
            {[t.artist || 'Unknown artist', genres[0]].filter(Boolean).join(' · ')}
          </span>
          <TrendStatusText status={t.trend_status} />
        </div>
        {/* Line 3: the 30-day curve, full width */}
        {spark && (
          <div className="pt-1" title={spark.kind === 'views' ? 'Daily views, last 30 days' : 'Daily rank, last 30 days (up is better)'}>
            <TrendSparkline values={spark.values} width={200} height={22} fluid label={sparkLabel} />
          </div>
        )}

        <div data-card-part="actions" className="mt-auto flex flex-col gap-1 pt-2" onClick={stop}>
          <PlaySongButton t={t} />
          <div className="flex items-center gap-1.5">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={onRemix}
              aria-haspopup="dialog"
              title="Make an original track in this song’s style — tempo, instruments, energy"
              className="h-8 min-w-0 flex-1 gap-1.5 rounded-[8px] border-[#d0d5dd] bg-white px-2 text-[12.5px] leading-[18px] text-[#6941c6] hover:bg-[#f9fafb] hover:text-[#6941c6]"
            >
              {/* The wand only when the card is wide enough for it next to the label. */}
              <Wand2 className="hidden h-3.5 w-3.5 shrink-0 @min-[216px]:block" aria-hidden />
              <span className="truncate">Remix</span>
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={onOpen} className="h-8 w-8 shrink-0 rounded-[8px] p-0 text-[#667085]" aria-label={`Details for ${t.title}`} title="Top videos, 30-day chart and clip IDs">
              <ChevronRight className="h-4 w-4" aria-hidden />
            </Button>
          </div>
        </div>
      </div>
    </article>
  );
}
